import React, { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import Seo from '../components/Seo';
import StatusTimeline from '../components/StatusTimeline';
import DocumentPanel from '../components/DocumentPanel';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { portalTranslations } from '../i18n/portalTranslations';
import { formatPortalDate, getShipmentById } from '../services/shipmentService';
import { Shipment } from '../types/shipment';
import './PortalPages.css';

const ShipmentDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { language } = useLanguage();
  const pt = portalTranslations[language].portal;
  const { user, loading } = useAuth();
  const [shipment, setShipment] = useState<Shipment | null>(null);
  const [loadingShipment, setLoadingShipment] = useState(true);
  const [forbidden, setForbidden] = useState(false);

  useEffect(() => {
    if (!id || !user || user.role !== 'client') {
      setLoadingShipment(false);
      return undefined;
    }

    let active = true;
    setLoadingShipment(true);
    getShipmentById(id)
      .then((row) => {
        if (!active) return;
        if (!row) {
          setShipment(null);
          return;
        }
        if (row.clientEmail.toLowerCase() !== user.email.toLowerCase()) {
          setForbidden(true);
          setShipment(null);
          return;
        }
        setShipment(row);
      })
      .finally(() => {
        if (active) setLoadingShipment(false);
      });

    return () => {
      active = false;
    };
  }, [id, user]);

  if (loading) {
    return (
      <div className="portal-page">
        <div className="container portal-inner portal-loading">
          {language === 'ar' ? 'جاري التحميل...' : 'Loading...'}
        </div>
      </div>
    );
  }

  if (!user || user.role !== 'client') {
    return <Navigate to="/portal" replace />;
  }

  return (
    <div className="portal-page">
      <Seo
        title={`${pt.declaration} | ${pt.title}`}
        description={pt.subtitle}
        path={`/portal/shipments/${id || ''}`}
      />
      <div className="container portal-inner portal-detail">
        <Link to="/portal" className="portal-back-inline">← {pt.backToList}</Link>

        {loadingShipment ? (
          <p className="portal-empty">{language === 'ar' ? 'جاري التحميل...' : 'Loading...'}</p>
        ) : forbidden || !shipment ? (
          <p className="portal-empty">{pt.noShipments}</p>
        ) : (
          <>
            <div className="portal-detail-header">
              <div>
                <p className="portal-updated">{pt.declaration}</p>
                <h1 className="portal-detail-title" dir="ltr">{shipment.declarationNumber}</h1>
              </div>
              <span className="portal-status-badge portal-status-badge-lg">
                {portalTranslations[language].tracker.steps[shipment.status]}
              </span>
            </div>

            <div className="portal-detail-meta">
              <p><strong>{pt.cargo}:</strong> {shipment.cargoDescription}</p>
              <p><strong>{pt.border}:</strong> {shipment.border}</p>
              <p className="portal-updated highlight-update">
                {pt.updated}: {formatPortalDate(shipment.updatedAt, language)}
              </p>
            </div>

            <h2>{pt.timeline}</h2>
            <StatusTimeline
              current={shipment.status}
              language={language}
              history={shipment.history}
              showNotes
            />

            <DocumentPanel shipmentId={shipment.id} canUpload={false} />
          </>
        )}
      </div>
    </div>
  );
};

export default ShipmentDetailPage;
